import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { omit } from "lodash";
import { FC, ReactElement, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { Error, MoreHoriz } from "@livechat/design-system-icons";
import {
  ActionMenu,
  ActionMenuItem,
  Button,
  DesignToken,
  Heading,
  Icon,
  ModalPortal,
  SpacingToken,
  Switch,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import TeamAvatarStack from "components/avatar-stack/TeamAvatarStack";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { SettingsTableColumn, SettingsTableRow } from "components/settings-table";
import useModal from "hooks/use-modal";
import { IAutomationViewMode } from "interfaces/automations";
import { AutomationsRoutes } from "routes/automations/constants";
import { validateActions, validateTriggers } from "routes/automations/shared/validators";
import { selectAgentsAlphabetically, selectIsAdmin } from "store/entities/agents";
import { selectCannedResponsesArray } from "store/entities/canned-responses";
import { selectMailboxesAlphabetically } from "store/entities/mailboxes/selectors";
import { RulesActions } from "store/entities/rules/actions";
import { IRule, IRuleTriggerTeamID } from "store/entities/rules/interfaces";
import { selectRulesMaxOrdering } from "store/entities/rules/selectors";
import { selectTagsAlphabetically } from "store/entities/tags";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { textEllipsisStyles, textMultilineEllipsisStyles } from "styles/common";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";
import { plural } from "utils/string";

import { EntryContainer } from "./styled";
import { textTruncateStyles } from "./styles";

interface IProps {
  rule: IRule;
  highlight: boolean;
  displayMode: IAutomationViewMode;
  hasScrolled: boolean;
  onScroll: () => void;
}

const WORKFLOW_DELETE_TEXT = "Delete";
const WORKFLOW_CANCEL_TEXT = "Cancel";

export const WorkflowCard: FC<IProps> = ({ rule, highlight, displayMode, hasScrolled, onScroll, ...props }) => {
  const history = useHistory();
  const isAdmin = useSelector(selectIsAdmin);
  const dispatch = useDispatch();
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const [deleteModal, deleteModalActions] = useModal(false);
  const teams = useSelector(selectTeamsAlphabetically);
  const agents = useSelector(selectAgentsAlphabetically);
  const cannedResponses = useSelector(selectCannedResponsesArray);
  const tags = useSelector(selectTagsAlphabetically);
  const maxOrdering = useSelector(selectRulesMaxOrdering);
  const mailboxes = useSelector(selectMailboxesAlphabetically);

  const hasError = useMemo(() => {
    return (
      validateActions(rule.actions, teams, agents, cannedResponses, tags).some(error => error) ||
      validateTriggers(rule.triggers, tags, teams, agents, mailboxes).some(error => error)
    );
  }, [rule]);

  const teamsTrigger = rule.triggers.find(trigger => trigger.type === "teamID") as IRuleTriggerTeamID | undefined;
  const teamIds = teamsTrigger?.in || [];
  const selectedTeamIds = teams.filter(team => teamIds.includes(team.id)).map(team => team.id);
  const allTeams = !teamsTrigger && rule.quantifier === "all";

  const onEdit = (): void => {
    history.push(`${AutomationsRoutes.EditRule}/${rule.id}`);
  };

  const handleEdit = (e: React.MouseEvent<HTMLDivElement>): void => {
    const targetText = (e.target as HTMLElement).innerText;
    if (targetText === WORKFLOW_CANCEL_TEXT || targetText === WORKFLOW_DELETE_TEXT) {
      return;
    }
    onEdit();
  };

  const handleDeleteModalClose = (): void => {
    deleteModalActions.close();
  };

  const showDeleteModal = (): void => {
    deleteModalActions.show(true);
  };

  const onDelete = (): void => {
    dispatch(RulesActions.delete({ id: rule.id }));
    deleteModalActions.close();
  };

  const onToggleActive = (): void => {
    dispatch(RulesActions.update({ activeChange: true, value: { id: rule.id, active: !rule.active } }));
  };

  const onDuplicate = (): void => {
    const duplicatedRule = omit(
      {
        ...rule,
        name: `Copy of ${rule.name}`,
        active: false,
        ordering: maxOrdering + 100,
      },
      "id",
      "licenseId",
      "useCounter",
      "premadeType"
    );

    history.push(AutomationsRoutes.AddRule, { duplicatedRule, duplicatedRuleId: rule.id });
  };

  useEffect(() => {
    if (highlight && scrollAnchorRef.current && !hasScrolled) {
      scrollAnchorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      scrollAnchorRef.current.focus();
      window.scrollBy(0, 1);
      onScroll();
    }
  }, [highlight, scrollAnchorRef, onScroll, hasScrolled]);

  if (displayMode === "list") {
    return (
      <>
        <Row hasError={hasError} onClick={handleEdit} highlight={highlight} {...props}>
          <Column>
            <StopPropagation css={fullWidthWrapperStyles}>
              <Switch size="compact" disabled={!isAdmin} on={rule.active} onChange={onToggleActive} />
            </StopPropagation>
          </Column>
          <Column>
            <Flex alignItems="center" gap={withVar(SpacingToken.Spacing2)} ref={scrollAnchorRef}>
              {hasError && (
                <Tooltip
                  triggerRenderer={() => (
                    <Icon source={Error} customColor={withVar(DesignToken.ContentBasicNegative)} />
                  )}
                >
                  <Text>There are errors in this rule</Text>
                </Tooltip>
              )}
              <Text bold css={textEllipsisStyles}>
                {rule.name}
              </Text>
            </Flex>
          </Column>
          <Column>
            <Text
              css={textEllipsisStyles}
              title={rule.description}
              customColor={withVar(DesignToken.ContentBasicSecondary)}
            >
              {rule.description || "No description"}
            </Text>
          </Column>
          <Column>
            {allTeams ? (
              <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>All teams</Text>
            ) : (
              <LeftAlignedAvatarStack teamIds={selectedTeamIds} />
            )}
          </Column>
          <Column>
            <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
              {rule.useCounter.toLocaleString()} {plural(rule.useCounter, "time", "times")}
            </Text>
          </Column>
          <Column actionMenu>
            <StopPropagation>
              <Menu onEdit={onEdit} onDuplicate={onDuplicate} onDelete={showDeleteModal} isAdmin={isAdmin} />
            </StopPropagation>
          </Column>
        </Row>
        {deleteModal && <DeleteModal onClose={handleDeleteModalClose} onDelete={onDelete} rule={rule} />}
      </>
    );
  }

  return (
    <EntryContainer
      ref={scrollAnchorRef}
      tabIndex={-1}
      id={rule.id}
      data-testid={`workflow-entry-${rule.id}`}
      onClick={handleEdit}
      onMouseDown={e => e.preventDefault()}
      data-error={hasError}
      {...props}
    >
      <Flex justifyContent="space-between" alignItems="center" width="100%">
        <Flex
          gap={withVar(SpacingToken.Spacing2)}
          css={css`
            overflow: hidden;
          `}
        >
          {hasError && (
            <Tooltip
              triggerRenderer={() => <Icon source={Error} customColor={withVar(DesignToken.ContentBasicNegative)} />}
            >
              <Text>There are errors in this rule</Text>
            </Tooltip>
          )}
          <CardHeader size="xs">{rule.name}</CardHeader>
        </Flex>
        <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)} onClick={e => e.stopPropagation()}>
          <Switch
            data-testid={`workflow-switch-${rule.id}`}
            size="compact"
            disabled={!isAdmin}
            on={rule.active}
            onChange={onToggleActive}
          />
          <Menu onEdit={onEdit} onDuplicate={onDuplicate} onDelete={showDeleteModal} isAdmin={isAdmin} />
        </Flex>
      </Flex>
      <Block minHeight="70px" flex="1" marginV={withVar(SpacingToken.Spacing3)}>
        {rule.description ? (
          <Text
            customColor={withVar(DesignToken.ContentBasicSecondary)}
            css={[textTruncateStyles, textMultilineEllipsisStyles(3)]}
          >
            {rule.description}
          </Text>
        ) : (
          <Text customColor={withVar(DesignToken.ContentBasicDisabled)}>No description</Text>
        )}
      </Block>
      <Flex justifyContent="space-between" alignItems="center" width="100%">
        {allTeams ? <Text size="sm">All teams</Text> : <LeftAlignedAvatarStack teamIds={selectedTeamIds} />}
        <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)}>
          Used {rule.useCounter.toLocaleString()} {plural(rule.useCounter, "time", "times")}
        </Text>
      </Flex>
      {deleteModal && <DeleteModal onClose={handleDeleteModalClose} onDelete={onDelete} rule={rule} />}
    </EntryContainer>
  );
};

const CardHeader = styled(Heading)`
  /* max-width: 300px; */
  padding-right: ${withVar(SpacingToken.Spacing4)};
  ${textEllipsisStyles}
`;

const Column: React.FC<{
  children: React.ReactNode;
  actionMenu?: boolean;
}> = ({ children, actionMenu }) => (
  <SettingsTableColumn
    paddingDesktop={actionMenu ? "0" : `${withVar(SpacingToken.Spacing3)} ${withVar(SpacingToken.Spacing5)}`}
    css={{
      minWidth: "0",
      ...(actionMenu && {
        display: "flex",
        alignItems: "center",
      }),
    }}
    className="settings-table-column"
  >
    {children}
  </SettingsTableColumn>
);

const Row: FC<{
  children: React.ReactNode;
  hasError: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  highlight?: boolean;
}> = ({ children, hasError, onClick, highlight }) => (
  <SettingsTableRow
    css={css`
      cursor: pointer;
      & > div {
        background-color: ${hasError ? withVar(DesignToken.SurfaceAccentEmphasisMinNegative) : undefined};
      }
    `}
    onClick={onClick}
    highlight={highlight}
  >
    {children}
  </SettingsTableRow>
);

const StopPropagation: FC<{ children: React.ReactNode }> = ({ children, ...props }) => (
  <div onClick={e => e.stopPropagation()} {...props}>
    {children}
  </div>
);

interface IMenuProps {
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  isAdmin: boolean;
}
const Menu: FC<IMenuProps> = ({ onEdit, onDuplicate, onDelete, isAdmin }): ReactElement => {
  return (
    <ActionMenu
      options={[
        {
          key: "edit",
          element: <ActionMenuItem>Edit</ActionMenuItem>,
          onClick: onEdit,
        },
        {
          key: "duplicate",
          element: <ActionMenuItem>Duplicate</ActionMenuItem>,
          onClick: onDuplicate,
        },
        {
          key: "delete",
          element: <ActionMenuItem kind="warning">Delete</ActionMenuItem>,
          onClick: onDelete,
        },
      ]}
      triggerRenderer={
        <Button
          kind="text"
          disabled={!isAdmin}
          icon={<Icon source={MoreHoriz} />}
          data-testid="action-menu-trigger-button"
          size="compact"
        />
      }
    />
  );
};

interface IDeleteModalProps {
  onClose: () => void;
  onDelete: () => void;
  rule: IRule;
}

const DeleteModal: FC<IDeleteModalProps> = ({ onClose, onDelete, rule }) => {
  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ConfirmActionModal
        heading="Delete rule?"
        onClose={onClose}
        primaryAction={{
          label: "Delete",
          onClick: onDelete,
          actionType: "destructive",
          dataTestId: WORKFLOW_DELETE_TEXT,
        }}
        secondaryAction={{
          label: WORKFLOW_CANCEL_TEXT,
          onClick: onClose,
        }}
      >
        <Text data-testid="delete-modal-text" css={{ textAlign: "center" }}>
          You&lsquo;re about to remove the {rule.active && "active"} <b>{rule.name}</b> rule.{" "}
          {rule.active && "This may have an impact on customer support."} Are you sure you want to continue?
        </Text>
      </ConfirmActionModal>
    </ModalPortal>
  );
};

const LeftAlignedAvatarStack = styled(TeamAvatarStack)`
  justify-content: flex-start;
  margin-left: ${withVar(SpacingToken.Spacing2)};
`;

const fullWidthWrapperStyles = css({ height: "100%", display: "flex", alignItems: "center" });
